// Shared address normalizer for duplicate-lookup detection.
//
// Mirrors the backend AVM-cache key (_avm_addr_key in backend/main.py: lowercase +
// collapse whitespace) and extends it with USPS-style abbreviation standardization so
// "1646 W Alomar Ave" matches "1646 West Alomar Avenue, Anaheim, CA 92802".
// Matching is exact-after-normalization on the street line — no fuzzy matching.
// This is the ONLY frontend address normalizer; don't write another one.

// Long form -> canonical short form, applied per whole token. Covers the suffixes and
// directionals RentCast / users actually produce; unknown tokens pass through unchanged.
const TOKEN_MAP = {
  street: "st",
  avenue: "ave",
  av: "ave",
  boulevard: "blvd",
  drive: "dr",
  driv: "dr",
  lane: "ln",
  road: "rd",
  court: "ct",
  circle: "cir",
  place: "pl",
  terrace: "ter",
  terr: "ter",
  parkway: "pkwy",
  highway: "hwy",
  trail: "trl",
  square: "sq",
  loop: "loop",
  way: "way",
  apartment: "apt",
  suite: "ste",
  unit: "unit",
  north: "n",
  south: "s",
  east: "e",
  west: "w",
  northeast: "ne",
  northwest: "nw",
  southeast: "se",
  southwest: "sw",
};

// Normalize a full address string: lowercase, strip punctuation, collapse whitespace,
// standardize abbreviations. ("1646 West Alomar Avenue" -> "1646 w alomar ave")
export function normalizeAddress(addr) {
  return String(addr || "")
    .toLowerCase()
    .replace(/[.,#]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => TOKEN_MAP[t] || t)
    .join(" ");
}

// A comma-segment that names a unit/apt/suite (e.g. "Unit 4", "#212", "Apt B"). Used to keep
// unit identity in the key so two condos at the same street address don't collide. Excludes
// 5-digit numbers so a bare ZIP segment isn't mistaken for a unit.
const UNIT_SEGMENT = /(\b(apt|ste|unit)\b)|(^\d{1,4}[a-z]?$)/;

// Comparison key: the street line (text before the first comma) PLUS any unit/apt/suite the
// address carries, normalized. City/state/zip formatting varies too much between user input
// and RentCast's formattedAddress for the full string to exact-match, so the street line +
// unit is the identity. We keep the unit because dropping it would let "123 Main St, Unit 4"
// and "123 Main St, Unit 8" collide and silently overwrite each other's saved data. Two
// properties on identical street lines in DIFFERENT cities still collide (accepted — the
// guard is a confirm dialog that shows the matched address, so the user can cancel).
export function addressMatchKey(addr) {
  const raw = String(addr || "");
  const segments = raw.split(",").map((s) => normalizeAddress(s)).filter(Boolean);
  if (segments.length === 0) return "";
  let key = segments[0]; // house number + street (+ unit if it sits on the first line)
  // Fold in any later segment that is a unit designator; drop city/state/zip segments.
  for (let i = 1; i < segments.length; i++) {
    if (UNIT_SEGMENT.test(segments[i])) key += " " + segments[i];
  }
  if (!raw.includes(",")) {
    // No commas at all ("1646 w alomar ave anaheim ca 92802") -> drop trailing state + zip.
    key = key.replace(/\s+[a-z]{2}\s+\d{5}(-\d{4})?$/, "").replace(/\s+\d{5}(-\d{4})?$/, "");
  }
  return key;
}

// True when two addresses identify the same property for guard/merge purposes.
export function sameAddress(a, b) {
  const ka = addressMatchKey(a);
  const kb = addressMatchKey(b);
  return ka !== "" && ka === kb;
}
